import _ from 'lodash';
import Vue from 'vue';

import store from '@/store';
import SetupApi from '@/store/api/setup';
import ContactAPI from '@/store/api/contact';
import CommentAPI from '@/store/api/comment';

import CategoryPost from '@/views/CategoryPost';
import BlogPageForm from '@/views/BlogPageForm';
import ServicePageForm from '@/views/ServicePageForm';

import PasswordResetForm from '@/views/PasswordResetForm';
import EmailVerificationForm from '@/views/EmailVerificationForm';

import { default as router, resetRouter } from '@/router';

// initial state
// shape: [{ id, quantity }]
const state = {
    loaded: false,
    ready: false,
    error: false,
    CMS: {},
    // config: {},
    messages: [],
    ui: {
        sidebar: false // sidebar visibility
    },
    uploadEndpoint: _.trim(`${process.env.VUE_APP_API_ENDPOINT}${process.env.VUE_APP_API_UPLOAD_ROUTE}`, '/')
};

// getters
const getters = {
    // auth: state => state.auth,
    loaded: state => state.loaded,
    ready: state => state.ready,
    error: state => state.error,
    CMS: state => state.CMS,
    // config: state => state.CMS.config,
    menus: state => state.CMS.menus,
    messages: state => state.messages,
    sidebar: state => state.ui.sidebar,
    
    logo: state => {
        let logo = {};
        if (state.CMS.images && state.CMS.images.length > 0) {
            logo = state.CMS.images.find(image => image.type === 'cms-logo');
        }
        return logo;
    },
    googleRecaptchaSiteKey: state => {
        const apiKeys = _.get(state.CMS, 'apiKeys') || [];
        const apiKey = apiKeys.find(apiKey => apiKey.key === 'google-recaptcha-v3');

        let googleRecaptchaSiteKey;
        if (apiKey) {
            googleRecaptchaSiteKey = apiKey.public;
        }
        return googleRecaptchaSiteKey;
    },
    uploadEndpoint: state => state.uploadEndpoint
};

// actions
const actions = {
    fetchSetup({ commit, dispatch }, { base_url }) {
        const fetchCms = SetupApi.fetchCms(base_url)
            .then(setup => {
                if (setup) {
                    commit('addSetupToState', setup);
                } else {
                    // throw new Error('There is no content for this domain.')
                    commit('setError', { code: 404, message: 'Not Found!' });
                }
            })
            .catch(error => commit('fetchFailed', error));

        let actions = [fetchCms];

        if (localStorage.getItem('member-token')) {
            const fetchAuthenticatedMember = dispatch('auth/fetchAuthenticatedMember');
            actions.push(fetchAuthenticatedMember);
        }

        return Promise.all(actions).then(() => {
            commit('mapRouter');
        });
    },
    // pageReady({ commit, state }) {
    //     commit('isReady', true)
    // },
    // pageNotReady({ commit, state }){
    //     commit('isReady', false)
    // },
    sendContactToApi({ commit }, payload) {
        const success_message = {
            id: _.uniqueId(),
            title: 'Erfolgreich',
            text: 'Ihre Nachricht wurde erfolgreich versendet.',
            type: 'success',
            autoclose: 5000
        };
        const error_message = {
            id: _.uniqueId(),
            title: 'Fehler',
            text: 'Ihre Nachricht konnte nicht versendet werden.',
            type: 'fail',
            autoclose: 5000
        };
        return ContactAPI.createContact(payload)
            .then(data => commit('addMessage', success_message))
            .catch(error => commit('addMessage', error_message));
    },
    sendCommentToApi({ commit }, payload) {
        let id = payload.page_id;
        let comment = {
            username: payload.username,
            email: payload.email,
            body: payload.body,
            rating: payload.rating
        };
        const success_message = {
            id: _.uniqueId(),
            title: 'Erfolgreich',
            text: 'Ihr Kommentar wurde erfolgreich gespeichert.',
            type: 'success',
            autoclose: 5000
        };
        const error_message = {
            id: _.uniqueId(),
            title: 'Fehler',
            text: 'Ihr Kommentar konnte nicht gespeichert werden.',
            type: 'fail',
            autoclose: 5000
        };
        return CommentAPI.createComment(id, comment)
            .then(data => commit('addMessage', success_message))
            .catch(error => commit('addMessage', error_message));
    }
};

// mutations
const mutations = {
    addSetupToState(state, cms) {
        state.loaded = true;
        // state.meta = data.setup.meta
        state.CMS = cms;
        // state.config = data.config
    },
    isReady(state, value) {
        if (value === true) state.loaded = true;

        state.ready = value;
    },
    updateSidebarVisibility(state, value) {
        state.ui.sidebar = value;
    },
    mapRouter(state) {
        let loadingRoutes = [];

        const staticRoutes = [
            {
                path: '/blog-posts/:key',
                name: 'Blog Beiträge',
                component: CategoryPost,
                meta: {
                    icon: null,
                    title: `${state.CMS.name} - Blog Beiträge`,
                    category: {
                        key: 'blog-posts'
                    }
                }
            },
            {
                path: '/blog-post/neu',
                name: 'Neuer Blog Beitrag',
                component: BlogPageForm,
                meta: {
                    icon: null,
                    title: `${state.CMS.name} - Blog Beitrag erstellen`,
                    skipMiddleware: true,
                    category: {
                        key: 'blog-posts'
                    }
                }
            },
            {
                path: '/digitale-werkzeuge/:key',
                name: 'Digitale Werkzeuge',
                component: CategoryPost,
                meta: {
                    icon: null,
                    title: `${state.CMS.name} - Digitale Werkzeuge`,
                    category: {
                        key: 'digitale-werkzeuge'
                    }
                }
            },
            {
                path: '/digitales-werkzeug/neu',
                name: 'Neues Digitales Werkzeug',
                component: ServicePageForm,
                meta: {
                    icon: null,
                    title: `${state.CMS.name} - Digitales Werkzeug erstellen`,
                    skipMiddleware: true,
                    category: {
                        key: 'digitale-werkzeuge'
                    }
                }
            },
            {
                path: '/resetpassword/:email/:token',
                name: 'Passwort zurücksetzen',
                component: PasswordResetForm,
                meta: {
                    icon: null,
                    title: `${state.CMS.name} - Passwort zurücksetzen`,
                    skipMiddleware: true
                }
            },
            {
                path: '/verify/:id/:hash/:expires/:signature',
                name: 'Account bestätigen',
                component: EmailVerificationForm,
                meta: {
                    icon: null,
                    title: `${state.CMS.name} - Account bestätigen`,
                    skipMiddleware: true
                }
            },
            {
                path: '/blog/:key/:src?',
                name: 'blog',
                // component: () => import('@/views/CategoryPost.vue'),
                meta: {
                    icon: null,
                    title: state.CMS.name ? `${state.CMS.name} - Blog` : 'Blog',
                    category: {
                        key: 'blog'
                    }
                }
            },
            {
                path: '*',
                name: 'Page Not Found',
                component: () => import('@/views/PageNotFound.vue'),
                meta: {
                    icon: null,
                    title: `${state.CMS.name} - Page Not Found`,
                    skipMiddleware: true
                }
            }
            // add static category routes
        ];

        const isAuthenticated = store.getters['auth/isAuthenticated'];

        if (state.CMS.menus && state.CMS.menus.length) {
            const nodeRoutes = data => {
                let routes = [],
                    items = data.items;
                _.each(items, (item, index) => {
                    if (item.restriction === 'members' && !isAuthenticated) return true;
                    else if (item.restriction === 'guests' && isAuthenticated) return true;
                    if (item.disabled) return true;

                    let children = null;
                    if (item.children) {
                        children = nodeRoutes({ items: item.children });
                    }

                    let component, view;
                    if (item.page && item.page.view) {
                        component = () => import(`@/views/${item.page.view}.vue`);
                        view = item.page.view;
                    }
                    const meta = {
                        rootIndex: index,
                        icon: item.icon,                        
                        title: state.CMS.name ? `${state.CMS.name} - ${item.title}` : item.title,
                        pageID: item.page?.id,
                        menuItemID: item.id,
                    };

                    switch (view) {
                        case 'Logout':
                            return true;
                        case 'Redirect':
                            const type = _.get(item.page, 'hyperlink.type');
                            const redirect = _.get(item.page, 'hyperlink.menuItem.path');
                            if (type === 'internal' && redirect) {
                                const node = {
                                    name: item.title || '',
                                    path: item.path,
                                    redirect,
                                    meta
                                };
                                routes.push(node);
                            }
                            break;

                        default:
                            const node = {
                                name: item.title || '',
                                path: item.path,
                                component,
                                meta,
                                children: children
                            };
                            routes.push(node);
                    }
                });
                return routes;
            };

            let dynamicRoutes = [];
            state.CMS.menus.forEach(menu => {
                nodeRoutes({ items: menu.menuItems }).forEach(item => dynamicRoutes.push(item));
            });

            _.each(dynamicRoutes, route => {
                loadingRoutes.push(route);
            });
        }

        _.each(staticRoutes, route => {
            loadingRoutes.push(route);
        });

        resetRouter();
        router.addRoutes(loadingRoutes);
    },
    fetchFailed(state, error) {
        /* eslint no-console: ["error", { allow: ["error"] }] */
        console.error(error);
    },
    setError(state, error) {
        state.error = error;
    },
    addMessage(state, message) {
        const messages = _.get(state, 'messages', []);
        const i = messages.length;

        const search = _.pick(message, ['title', 'text', 'info', 'type']);
        const existing = _.findIndex(messages, search);
        if (existing >= 0) {
            Vue.set(state.messages[existing], 'autoclose', _.get(message, 'autoclose'));
            Vue.set(state.messages[existing], 'highlight', true);
            setTimeout(() => {
                if (_.has(state.messages[existing], 'highlight')) Vue.set(state.messages[existing], 'highlight', false);
            }, 200);
        } else {
            Vue.set(state.messages, i, message);
        }
    },

    removeMessage(state, id) {
        const message = _.find(state.messages, { id });
        const i = _.findIndex(state.messages, message);

        if (i !== -1) {
            Vue.delete(state.messages, i);
        }
    }
};

export default {
    state,
    getters,
    actions,
    mutations
};
